import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useFilterStore } from '@/stores/filterStore';
import { useDashboardAlerts, useMultiLocationSummary } from './hooks/useDashboard';
import { AlertCard } from './components/AlertCard';
import { UsageCard } from './components/UsageCard';
import { LocationSummaryTable } from './components/LocationSummaryTable';
import { EmptyDashboard } from './components/EmptyDashboard';
import { DashboardSkeleton } from './components/DashboardSkeleton';
import { Card } from '@/shared/components/ui/Card';
import type { DashboardAlert } from '@/shared/types/domain.types';

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setFilters = useFilterStore((state) => state.setFilters);

  // Get user's location IDs
  const locationIds = user?.locations?.map((l) => l.id) || [];
  const isSingleLocation = locationIds.length === 1;
  const isMultiLocation = locationIds.length > 1;

  // Fetch dashboard data
  const {
    data: alerts = [],
    isLoading: alertsLoading,
    error: alertsError,
    refetch: refetchAlerts,
  } = useDashboardAlerts(locationIds);

  const {
    data: locationSummary = [],
    isLoading: summaryLoading,
  } = useMultiLocationSummary(locationIds, isMultiLocation);

  /**
   * Handle alert click - navigate to appropriate page with filters applied
   * Universal rule: Always pre-filter by THAT location and unselect all others
   */
  const handleAlertClick = (alert: DashboardAlert) => {
    const { location_id, type, target_page } = alert;

    // Set filters based on alert type
    switch (type) {
      case 'OVERDUE_ORDERED':
        // Navigate to Receiving page with status=ORDERED
        // The Receiving page will handle sorting by oldest first
        setFilters({
          location_ids: [location_id],
          status: ['ORDERED'],
          manufacturer_ids: [],
          order_type: undefined,
          date_from: undefined,
          date_to: undefined,
          search: undefined,
        });
        navigate('/receiving');
        break;

      case 'RECEIVING_BACKLOG':
        // Navigate to Receiving page with status=ORDERED (backlog items)
        setFilters({
          location_ids: [location_id],
          status: ['ORDERED'],
          manufacturer_ids: [],
          order_type: undefined,
          date_from: undefined,
          date_to: undefined,
          search: undefined,
        });
        navigate('/receiving');
        break;

      case 'STALE_PENDING':
        // Navigate to Weekly Ordering page with status=PENDING
        setFilters({
          location_ids: [location_id],
          status: ['PENDING'],
          manufacturer_ids: [],
          order_type: undefined,
          date_from: undefined,
          date_to: undefined,
          search: undefined,
        });
        navigate('/weekly-ordering');
        break;

      case 'ORDERING_DAY':
        // Navigate to Weekly Ordering page (location filter only)
        setFilters({
          location_ids: [location_id],
          status: undefined,
          manufacturer_ids: [],
          order_type: undefined,
          date_from: undefined,
          date_to: undefined,
          search: undefined,
        });
        navigate('/weekly-ordering');
        break;

      case 'LOW_USAGE':
        // Navigate to Reports page with location filter and scroll to Usage section
        // Use URL params: locationId for filtering, section=usage for scrolling
        navigate(`/reports?locationId=${location_id}&section=usage`);
        break;

      default:
        if (target_page) {
          navigate(`/${target_page}`);
        }
    }
  };

  /**
   * Handle location summary table click
   * Universal rule: Always pre-filter by THAT location and unselect all others
   */
  const handleLocationClick = (
    locationId: string,
    view: 'receiving' | 'weekly-ordering' | 'reports',
    section?: 'usage' | 'activity'
  ) => {
    switch (view) {
      case 'receiving':
        // Navigate to Receiving page with location filter and status=ORDERED
        setFilters({
          location_ids: [locationId],
          status: ['ORDERED'],
          manufacturer_ids: [],
          order_type: undefined,
          date_from: undefined,
          date_to: undefined,
          search: undefined,
        });
        navigate('/receiving');
        break;

      case 'weekly-ordering':
        // Navigate to Weekly Ordering page with location filter and status=PENDING
        setFilters({
          location_ids: [locationId],
          status: ['PENDING'],
          manufacturer_ids: [],
          order_type: undefined,
          date_from: undefined,
          date_to: undefined,
          search: undefined,
        });
        navigate('/weekly-ordering');
        break;

      case 'reports': {
        // Navigate to Reports page with location filter and optional section scroll
        const sectionParam = section ? `&section=${section}` : '&section=usage';
        navigate(`/reports?locationId=${locationId}${sectionParam}`);
        break;
      }
    }
  };

  /**
   * Split alerts into categories
   */
  const criticalAlerts = alerts.filter(
    (a) => a.type === 'OVERDUE_ORDERED' || a.type === 'STALE_PENDING' || a.type === 'RECEIVING_BACKLOG'
  );
  const orderingDayAlerts = alerts.filter((a) => a.type === 'ORDERING_DAY');
  const usageAlerts = alerts.filter((a) => a.type === 'LOW_USAGE');

  const hasAnyAlerts = alerts.length > 0;

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
        {isSingleLocation && user?.locations?.[0] && (
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            <span className="hidden lg:inline">{user.locations[0].name}</span>
            <span className="lg:hidden">{user.locations[0].short_code || user.locations[0].name}</span>
          </p>
        )}
      </div>

      {/* Error state */}
      {alertsError && (
        <Card padding="lg" className="text-center mb-6">
          <p className="text-red-600 mb-4">Failed to load dashboard data</p>
          <button
            onClick={() => refetchAlerts()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </Card>
      )}

      {/* Loading state - show skeleton only while loading alerts */}
      {alertsLoading && <DashboardSkeleton />}

      {/* Content - show as soon as alerts are loaded */}
      {!alertsLoading && (
        <>
          {/* Empty state - no alerts */}
          {!hasAnyAlerts && <EmptyDashboard />}

      {/* Has alerts - show them */}
      {hasAnyAlerts && (
        <div className="space-y-8">
          {/* Critical alerts section */}
          {criticalAlerts.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Action Required</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {criticalAlerts.map((alert) => (
                  <AlertCard
                    key={`${alert.type}-${alert.location_id}`}
                    alert={alert}
                    onClick={() => handleAlertClick(alert)}
                    showLocationName={isMultiLocation}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Ordering day alerts */}
          {orderingDayAlerts.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Ordering Reminders</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {orderingDayAlerts.map((alert) => (
                  <AlertCard
                    key={`${alert.type}-${alert.location_id}`}
                    alert={alert}
                    onClick={() => handleAlertClick(alert)}
                    showLocationName={isMultiLocation}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Usage alerts (lower priority) */}
          {usageAlerts.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold mb-4">System Usage</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {usageAlerts.map((alert) => (
                  <UsageCard
                    key={`${alert.type}-${alert.location_id}`}
                    usagePercentage={alert.count}
                    severity={alert.severity}
                    locationName={alert.location_name}
                    onClick={() => handleAlertClick(alert)}
                    showLocationName={isMultiLocation}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Multi-location summary table */}
          {isMultiLocation && (
            <div>
              {summaryLoading ? (
                <Card padding="lg" className="text-center">
                  <p className="text-gray-600">Loading location summary...</p>
                </Card>
              ) : locationSummary.length > 0 ? (
                <LocationSummaryTable
                  summaries={locationSummary}
                  onLocationClick={handleLocationClick}
                />
              ) : null}
            </div>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}
